import { z } from 'zod';
import { NonEmptyStringSchema } from './common';

export const BRAND_CONTEXT_VALUE_SOURCES = [
  'observed',
  'inferred',
  'operator',
] as const;

export const VERTICALS = [
  'supplements_ingestible',
  'food_beverage',
  'grooming_personal_care',
  'beauty_skincare',
  'apparel_basics',
  'apparel_fashion',
  'shapewear_intimates',
  'accessories',
  'home_goods',
  'electronics_durables',
  'services_digital',
  'other',
] as const;

export const PRODUCT_TYPES = [
  'consumable_replenishable',
  'wearable_sized',
  'durable_oneoff',
  'bundle_kit',
  'digital',
] as const;

export const PRICE_TIERS_USD = [
  'impulse_lt25',
  'value_25_75',
  'considered_75_200',
  'premium_200_500',
  'luxury_gt500',
] as const;

export const GROSS_MARGIN_BANDS = [
  'thin_lt40',
  'moderate_40_60',
  'healthy_60_75',
  'fat_gt75',
] as const;

export const BRAND_POSITIONS = [
  'value_discount',
  'mainstream_quality',
  'premium_aspirational',
  'luxury_exclusive',
  'clinical_authority',
  'lifestyle_community',
] as const;

export const BRAND_VOICES = [
  'clinical_credible',
  'warm_supportive',
  'bold_irreverent',
  'premium_minimal',
  'hype_urgent',
  'editorial_storytelling',
] as const;

export const CUSTOMER_SOPHISTICATION_LEVELS = [
  'novice_to_category',
  'experienced_in_category',
] as const;

export const PRIMARY_TRAFFIC_SOURCES = [
  'cold_paid_social',
  'warm_retargeting',
  'branded_search',
  'nonbrand_seo',
  'marketplace_browse',
  'email_sms_crm',
  'influencer_affiliate',
] as const;

export const AWARENESS_STAGES = [
  'problem_unaware',
  'problem_aware',
  'solution_aware',
  'product_aware',
  'most_aware',
] as const;

export const PAGE_GOAL_PRIORITIES = [
  'CVR',
  'AOV',
  'LTV_subscription',
  'lead_capture',
] as const;

export const REPEAT_PURCHASE_MODELS = [
  'subscription_native',
  'replenishable_nonsub',
  'occasional_repeat',
  'oneoff',
] as const;

export const CHANNELS = [
  'dtc_shopify',
  'dtc_headless',
  'amazon',
  'marketplace_other',
  'retail_dotcom',
] as const;

export const REGULATORY_CLASSES = [
  'none',
  'supplement_dshea',
  'cosmetic',
  'food',
  'medical_device',
  'financial',
] as const;

export const REVIEW_VOLUME_BANDS = [
  'none',
  'lt100',
  '100_5k',
  '5k_50k',
  'gt50k',
] as const;

export const PAGE_TYPES = [
  'buybox_first',
  'authority_longform',
  'advertorial',
  'collection_to_pdp',
] as const;

export const FIT_MATRIX_CONFIDENCE_LEVELS = [
  'HIGH',
  'MEDIUM',
  'LOW',
] as const;

export const BrandContextValueSourceEnum = z.enum(
  BRAND_CONTEXT_VALUE_SOURCES,
);
export const VerticalEnum = z.enum(VERTICALS);
export const ProductTypeEnum = z.enum(PRODUCT_TYPES);
export const PriceTierUsdEnum = z.enum(PRICE_TIERS_USD);
export const GrossMarginBandEnum = z.enum(GROSS_MARGIN_BANDS);
export const BrandPositionEnum = z.enum(BRAND_POSITIONS);
export const BrandVoiceEnum = z.enum(BRAND_VOICES);
export const CustomerSophisticationEnum = z.enum(
  CUSTOMER_SOPHISTICATION_LEVELS,
);
export const PrimaryTrafficSourceEnum = z.enum(PRIMARY_TRAFFIC_SOURCES);
export const AwarenessStageEnum = z.enum(AWARENESS_STAGES);
export const PageGoalPriorityEnum = z.enum(PAGE_GOAL_PRIORITIES);
export const RepeatPurchaseModelEnum = z.enum(REPEAT_PURCHASE_MODELS);
export const ChannelEnum = z.enum(CHANNELS);
export const RegulatoryClassEnum = z.enum(REGULATORY_CLASSES);
export const ReviewVolumeBandEnum = z.enum(REVIEW_VOLUME_BANDS);
export const PageTypeEnum = z.enum(PAGE_TYPES);
export const FitMatrixConfidenceEnum = z.enum(FIT_MATRIX_CONFIDENCE_LEVELS);

export const BrandContextFieldBaseSchema = z
  .object({
    source: BrandContextValueSourceEnum,
    confidence: z.number().min(0).max(1),
    evidence: z.array(NonEmptyStringSchema),
  })
  .strict();

export const BrandContextStringFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: NonEmptyStringSchema,
  }).strict();

export const BrandContextNumberFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: z.number(),
  }).strict();

export const BrandContextBooleanFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: z.boolean(),
  }).strict();

export const BrandContextVerticalFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: VerticalEnum,
  }).strict();

export const BrandContextProductTypeFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: ProductTypeEnum,
  }).strict();

export const BrandContextPriceTierUsdFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: PriceTierUsdEnum,
  }).strict();

export const BrandContextGrossMarginBandFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: GrossMarginBandEnum,
  }).strict();

export const BrandContextPositioningFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: BrandPositionEnum,
  }).strict();

export const BrandContextBrandVoiceFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: z.array(BrandVoiceEnum).min(1),
  }).strict();

export const BrandContextSophisticationFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: CustomerSophisticationEnum,
  }).strict();

export const BrandContextPrimaryTrafficFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: PrimaryTrafficSourceEnum,
  }).strict();

export const BrandContextAwarenessStageFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: AwarenessStageEnum,
  }).strict();

export const BrandContextPageGoalPriorityFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: z.array(PageGoalPriorityEnum).min(1),
  }).strict();

export const BrandContextRepeatPurchaseFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: RepeatPurchaseModelEnum,
  }).strict();

export const BrandContextChannelFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: ChannelEnum,
  }).strict();

export const BrandContextRegulatoryClassFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: RegulatoryClassEnum,
  }).strict();

export const BrandContextReviewVolumeFieldSchema =
  BrandContextFieldBaseSchema.extend({
    value: ReviewVolumeBandEnum,
  }).strict();

export const BrandPageContextSchema = z
  .object({
    brand_id: BrandContextStringFieldSchema,
    product_name: BrandContextStringFieldSchema,
    vertical: BrandContextVerticalFieldSchema,
    product_type: BrandContextProductTypeFieldSchema,
    price_tier_usd: BrandContextPriceTierUsdFieldSchema,
    aov_target_usd: BrandContextNumberFieldSchema,
    gross_margin_band: BrandContextGrossMarginBandFieldSchema,
    positioning: BrandContextPositioningFieldSchema,
    brand_voice: BrandContextBrandVoiceFieldSchema,
    customer_target: z
      .object({
        demographic: BrandContextStringFieldSchema,
        jtbd: BrandContextStringFieldSchema,
        sophistication: BrandContextSophisticationFieldSchema,
      })
      .strict(),
    primary_traffic: BrandContextPrimaryTrafficFieldSchema,
    awareness_stage: BrandContextAwarenessStageFieldSchema,
    page_goal_priority: BrandContextPageGoalPriorityFieldSchema,
    repeat_purchase: BrandContextRepeatPurchaseFieldSchema,
    channel: BrandContextChannelFieldSchema,
    regulatory_class: BrandContextRegulatoryClassFieldSchema,
    assets: z
      .object({
        review_volume: BrandContextReviewVolumeFieldSchema,
        ugc_video_available: BrandContextBooleanFieldSchema,
        celebrity_or_athlete_access: BrandContextBooleanFieldSchema,
        clinical_substantiation: BrandContextBooleanFieldSchema,
        endorsement_budget: BrandContextBooleanFieldSchema,
        loyalty_program_exists: BrandContextBooleanFieldSchema,
      })
      .strict(),
    brand_rules: z
      .object({
        no_discount_brand: BrandContextBooleanFieldSchema,
        gwp_margin_ceiling_pct: BrandContextNumberFieldSchema,
        bnpl_enabled: BrandContextBooleanFieldSchema,
      })
      .strict(),
  })
  .strict();

export const ExemplarContextAssetsSchema = z
  .object({
    review_volume: ReviewVolumeBandEnum.optional(),
    ugc_video_available: z.boolean().optional(),
    celebrity_or_athlete_access: z.boolean().optional(),
    clinical_substantiation: z.boolean().optional(),
    endorsement_budget: z.boolean().optional(),
    loyalty_program_exists: z.boolean().optional(),
  })
  .strict();

export const ExemplarContextCustomerTargetSchema = z
  .object({
    demographic: NonEmptyStringSchema.optional(),
    jtbd: NonEmptyStringSchema.optional(),
    sophistication: CustomerSophisticationEnum.optional(),
  })
  .strict();

export const ExemplarContextSchema = z
  .object({
    vertical: VerticalEnum,
    product_type: ProductTypeEnum,
    price_tier: PriceTierUsdEnum,
    positioning: BrandPositionEnum,
    brand_voice: BrandVoiceEnum,
    primary_traffic: PrimaryTrafficSourceEnum,
    awareness_stage: AwarenessStageEnum,
    repeat_purchase: RepeatPurchaseModelEnum,
    channel: ChannelEnum,
    regulatory_class: RegulatoryClassEnum.optional(),
    page_goal_priority: z.array(PageGoalPriorityEnum).min(1),
    customer_target: ExemplarContextCustomerTargetSchema.optional(),
    assets: ExemplarContextAssetsSchema.optional(),
  })
  .strict();

export const ExemplarProfileSchema = z
  .object({
    id: NonEmptyStringSchema,
    context: ExemplarContextSchema,
    page_type: PageTypeEnum,
    copy_signature: NonEmptyStringSchema,
    uxui_signature: NonEmptyStringSchema,
    style_signature: NonEmptyStringSchema,
    use_as_template_when: NonEmptyStringSchema,
  })
  .strict();

export const FitMatrixConditionAtomSchema = z.union([
  NonEmptyStringSchema,
  z.number(),
  z.boolean(),
]);

export const FitMatrixConditionValueSchema = z.union([
  FitMatrixConditionAtomSchema,
  z.array(FitMatrixConditionAtomSchema).min(1),
]);

export const FitMatrixConditionSchema = z.record(
  NonEmptyStringSchema,
  FitMatrixConditionValueSchema,
);

export const FitMatrixContraindicationSchema = z.union([
  NonEmptyStringSchema,
  FitMatrixConditionSchema,
]);

export const FitMatrixRecordSchema = z
  .object({
    component_id: NonEmptyStringSchema.optional(),
    fits_when: z.union([z.literal('ALL'), FitMatrixConditionSchema]),
    contraindications: z.array(FitMatrixContraindicationSchema),
    prerequisites: z.array(NonEmptyStringSchema),
    copy_spec: NonEmptyStringSchema,
    uxui_spec: NonEmptyStringSchema,
    style_spec: NonEmptyStringSchema,
    confidence: FitMatrixConfidenceEnum,
  })
  .strict();

export type BrandContextValueSource =
  (typeof BRAND_CONTEXT_VALUE_SOURCES)[number];
export type Vertical = (typeof VERTICALS)[number];
export type ProductType = (typeof PRODUCT_TYPES)[number];
export type PriceTierUsd = (typeof PRICE_TIERS_USD)[number];
export type GrossMarginBand = (typeof GROSS_MARGIN_BANDS)[number];
export type BrandPosition = (typeof BRAND_POSITIONS)[number];
export type BrandVoice = (typeof BRAND_VOICES)[number];
export type CustomerSophistication =
  (typeof CUSTOMER_SOPHISTICATION_LEVELS)[number];
export type PrimaryTrafficSource =
  (typeof PRIMARY_TRAFFIC_SOURCES)[number];
export type AwarenessStage = (typeof AWARENESS_STAGES)[number];
export type PageGoalPriority = (typeof PAGE_GOAL_PRIORITIES)[number];
export type RepeatPurchaseModel =
  (typeof REPEAT_PURCHASE_MODELS)[number];
export type Channel = (typeof CHANNELS)[number];
export type RegulatoryClass = (typeof REGULATORY_CLASSES)[number];
export type ReviewVolumeBand = (typeof REVIEW_VOLUME_BANDS)[number];
export type PageType = (typeof PAGE_TYPES)[number];
export type FitMatrixConfidence =
  (typeof FIT_MATRIX_CONFIDENCE_LEVELS)[number];
export type BrandContextFieldBase = z.infer<
  typeof BrandContextFieldBaseSchema
>;
export type BrandPageContext = z.infer<typeof BrandPageContextSchema>;
export type ExemplarContext = z.infer<typeof ExemplarContextSchema>;
export type ExemplarProfile = z.infer<typeof ExemplarProfileSchema>;
export type FitMatrixConditionAtom = z.infer<
  typeof FitMatrixConditionAtomSchema
>;
export type FitMatrixConditionValue = z.infer<
  typeof FitMatrixConditionValueSchema
>;
export type FitMatrixCondition = z.infer<
  typeof FitMatrixConditionSchema
>;
export type FitMatrixContraindication = z.infer<
  typeof FitMatrixContraindicationSchema
>;
export type FitMatrixRecord = z.infer<typeof FitMatrixRecordSchema>;
